import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import {
  AlertCircle, Loader, FileText, RefreshCw, CheckCircle, XCircle,
  AlertTriangle, Image, Type, Tag, DollarSign, Star, TrendingUp,
  ChevronDown, ChevronUp
} from 'lucide-react';

const MAGENTO_PRODUCT_TYPES = [
  { value: 'simple', label: 'Simple' },
  { value: 'configurable', label: 'Configurable' },
  { value: 'grouped', label: 'Grouped' },
  { value: 'bundle', label: 'Bundle' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'downloadable', label: 'Downloadable' },
];

const ProductQuality = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedType, setSelectedType] = useState('');
  const [includeInvisible, setIncludeInvisible] = useState(false);
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const pageSize = 50;

  useEffect(() => {
    setData(null);
    setError(null);
    setSelectedType('');
    setIncludeInvisible(false);
    setIncludeDisabled(false);
    setCurrentPage(1);
  }, [selectedSite]);

  const fetchProductQuality = async (page = 1, typeId = selectedType, invisible = includeInvisible, disabled = includeDisabled) => {
    if (!selectedSite) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({ siteId: selectedSite.id, pageSize, page });
      if (typeId) params.set('typeId', typeId);
      if (invisible) params.set('includeInvisible', '1');
      if (disabled) params.set('includeDisabled', '1');

      const response = await fetch(
        `/app/api/analytics/product-quality.php?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setData(result.data);
        setCurrentPage(result.data.pagination?.current_page || 1);
        setTotalPages(result.data.pagination?.total_pages || 1);
      } else {
        setError(result.message || 'Failed to fetch product quality data');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchProductQuality(page);
    }
  };

  const handleTypeSelect = (typeId) => {
    setSelectedType(typeId);
    setCurrentPage(1);
    setData(null);
    fetchProductQuality(1, typeId, includeInvisible, includeDisabled);
  };

  const handleInvisibleToggle = (checked) => {
    setIncludeInvisible(checked);
    if (selectedType) fetchProductQuality(1, selectedType, checked, includeDisabled);
  };

  const handleDisabledToggle = (checked) => {
    setIncludeDisabled(checked);
    if (selectedType) fetchProductQuality(1, selectedType, includeInvisible, checked);
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 90) return 'bg-green-100 border-green-300';
    if (score >= 70) return 'bg-yellow-100 border-yellow-300';
    if (score >= 50) return 'bg-orange-100 border-orange-300';
    return 'bg-red-100 border-red-300';
  };

  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 70) return 'Good';
    if (score >= 50) return 'Needs Work';
    return 'Poor';
  };

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'high':
        return <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">High</span>;
      case 'medium':
        return <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded text-xs">Medium</span>;
      default:
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">Low</span>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="h-8 w-8 text-blue-600" />
              Product Content Quality
            </h1>
            <p className="text-gray-600 mt-2">Analyze product descriptions, images, and SEO attributes</p>
          </div>
          {data && (
            <button
              onClick={() => fetchProductQuality(currentPage)}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          )}
        </div>

        {/* Product type selector */}
        {!selectedSite ? (
          <div className="rounded-lg bg-red-50 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">Please select a site</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">Select product type to analyse</h2>
            <div className="flex flex-wrap gap-3">
              {MAGENTO_PRODUCT_TYPES.map(type => (
                <button
                  key={type.value}
                  onClick={() => handleTypeSelect(type.value)}
                  disabled={loading}
                  className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                    selectedType === type.value
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {type.label}
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 mt-4 cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={includeInvisible}
                onChange={e => handleInvisibleToggle(e.target.checked)}
                disabled={loading}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">Include products not visible individually</span>
            </label>
            <label className="flex items-center gap-2 mt-3 cursor-pointer w-fit">
              <input
                type="checkbox"
                checked={includeDisabled}
                onChange={e => handleDisabledToggle(e.target.checked)}
                disabled={loading}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-600">Include disabled products</span>
            </label>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Analyzing {MAGENTO_PRODUCT_TYPES.find(t => t.value === selectedType)?.label} products...</p>
            </div>
          </div>
        )}

        {/* Error (not shown when no site — handled above) */}
        {error && !loading && selectedSite && (
          <div className="rounded-lg bg-red-50 p-4 flex items-center gap-3 mb-6">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {!loading && data && (
          <div className="space-y-6">
            {/* Overall Score */}
            <div className={`p-6 rounded-lg border-2 ${getScoreBg(data.summary?.avg_score || 0)}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Average Content Quality Score</h2>
                  <p className="text-sm text-gray-600">Based on {data.summary?.total_analyzed || 0} products analyzed</p>
                </div>
                <div className="text-right">
                  <div className={`text-5xl font-bold ${getScoreColor(data.summary?.avg_score || 0)}`}>
                    {data.summary?.avg_score || 0}
                  </div>
                  <div className={`text-sm font-medium ${getScoreColor(data.summary?.avg_score || 0)}`}>
                    {getScoreLabel(data.summary?.avg_score || 0)}
                  </div>
                </div>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-gray-500">Excellent (90+)</span>
                </div>
                <p className="text-2xl font-bold text-green-600">{data.summary?.excellent || 0}</p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-yellow-600" />
                  <span className="text-sm text-gray-500">Good (70-89)</span>
                </div>
                <p className="text-2xl font-bold text-yellow-600">{data.summary?.good || 0}</p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-5 w-5 text-orange-600" />
                  <span className="text-sm text-gray-500">Needs Work (50-69)</span>
                </div>
                <p className="text-2xl font-bold text-orange-600">{data.summary?.needs_improvement || 0}</p>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <span className="text-sm text-gray-500">Poor (&lt;50)</span>
                </div>
                <p className="text-2xl font-bold text-red-600">{data.summary?.poor || 0}</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Type className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Avg Description Length</p>
                    <p className="text-xl font-bold text-gray-900">{data.summary?.avg_description_length || 0} chars</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <Image className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Products Without Images</p>
                    <p className="text-xl font-bold text-red-600">{data.summary?.products_without_images || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <FileText className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Products Without Description</p>
                    <p className="text-xl font-bold text-orange-600">{data.summary?.products_without_description || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => setActiveTab('overview')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 ${
                      activeTab === 'overview'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    All Products
                  </button>
                  <button
                    onClick={() => setActiveTab('issues')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 ${
                      activeTab === 'issues'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Issues by Type
                  </button>
                </nav>
              </div>

              <div className="p-6">
                {/* All Products Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-3">
                    {/* Top Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pb-4 border-b border-gray-200 mb-4">
                        <div className="text-sm text-gray-500">
                          Page {currentPage} of {totalPages}
                          {data.pagination?.total_products && (
                            <span className="ml-2">({data.pagination.total_products} total products)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`px-3 py-1 rounded text-sm ${
                              currentPage === 1
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Previous
                          </button>
                          {[...Array(Math.min(5, totalPages))].map((_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`px-3 py-1 rounded text-sm ${
                                  currentPage === pageNum
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`px-3 py-1 rounded text-sm ${
                              currentPage === totalPages
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                    {data.products?.length > 0 ? (
                      data.products.map((product) => (
                        <div key={product.id} className="border rounded-lg overflow-hidden">
                          <div
                            onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)}
                            className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getScoreBg(product.score)}`}>
                                <span className={`text-lg font-bold ${getScoreColor(product.score)}`}>
                                  {product.score}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{product.name}</p>
                                <p className="text-sm text-gray-500">
                                  <code className="bg-gray-100 px-1 rounded">{product.sku}</code>
                                  <span className="mx-2">•</span>
                                  {product.issues.length} issue{product.issues.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="hidden md:flex items-center gap-3 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Image className="h-4 w-4" />
                                  {product.image_count}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Type className="h-4 w-4" />
                                  {product.description_length}
                                </span>
                              </div>
                              {expandedProduct === product.id ? (
                                <ChevronUp className="h-5 w-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="h-5 w-5 text-gray-400" />
                              )}
                            </div>
                          </div>

                          {expandedProduct === product.id && (
                            <div className="border-t bg-gray-50 p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Issues */}
                                {product.issues.length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium text-gray-700 mb-2">Issues</p>
                                    <div className="space-y-2">
                                      {product.issues.map((issue, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-sm">
                                          <XCircle className="h-4 w-4 text-red-500" />
                                          <span className="text-gray-700">{issue.message}</span>
                                          {getSeverityBadge(issue.severity)}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Positives */}
                                {product.positives.length > 0 && (
                                  <div>
                                    <p className="text-sm font-medium text-gray-700 mb-2">Good</p>
                                    <div className="space-y-2">
                                      {product.positives.map((positive, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-sm">
                                          <CheckCircle className="h-4 w-4 text-green-500" />
                                          <span className="text-gray-700">{positive}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Stats */}
                              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">Type:</span>
                                  <span className="ml-2 text-gray-900 capitalize">{product.type}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Price:</span>
                                  <span className="ml-2 text-gray-900">${parseFloat(product.price || 0).toFixed(2)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Description:</span>
                                  <span className="ml-2 text-gray-900">{product.description_length} chars</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Images:</span>
                                  <span className="ml-2 text-gray-900">{product.image_count}</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p>No products analyzed</p>
                      </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4">
                        <div className="text-sm text-gray-500">
                          Page {currentPage} of {totalPages}
                          {data.pagination?.total_products && (
                            <span className="ml-2">({data.pagination.total_products} total products)</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`px-3 py-1 rounded text-sm ${
                              currentPage === 1
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Previous
                          </button>
                          {[...Array(Math.min(5, totalPages))].map((_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`px-3 py-1 rounded text-sm ${
                                  currentPage === pageNum
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`px-3 py-1 rounded text-sm ${
                              currentPage === totalPages
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Issues by Type Tab */}
                {activeTab === 'issues' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* No Description */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <FileText className="h-5 w-5 text-red-600" />
                        <h4 className="font-medium text-gray-900">No Description</h4>
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">
                          {data.issues?.no_description?.length || 0}
                        </span>
                      </div>
                      {data.issues?.no_description?.length > 0 ? (
                        <ul className="space-y-1 text-sm max-h-40 overflow-y-auto">
                          {data.issues.no_description.map((item, idx) => (
                            <li key={idx} className="text-gray-600">
                              <code className="text-xs bg-gray-100 px-1 rounded">{item.sku}</code> - {item.name}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-green-600">No issues found</p>
                      )}
                    </div>

                    {/* No Images */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Image className="h-5 w-5 text-red-600" />
                        <h4 className="font-medium text-gray-900">No Images</h4>
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">
                          {data.issues?.no_images?.length || 0}
                        </span>
                      </div>
                      {data.issues?.no_images?.length > 0 ? (
                        <ul className="space-y-1 text-sm max-h-40 overflow-y-auto">
                          {data.issues.no_images.map((item, idx) => (
                            <li key={idx} className="text-gray-600">
                              <code className="text-xs bg-gray-100 px-1 rounded">{item.sku}</code> - {item.name}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-green-600">No issues found</p>
                      )}
                    </div>

                    {/* No Meta Title */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Tag className="h-5 w-5 text-yellow-600" />
                        <h4 className="font-medium text-gray-900">No Meta Title</h4>
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                          {data.issues?.no_meta_title?.length || 0}
                        </span>
                      </div>
                      {data.issues?.no_meta_title?.length > 0 ? (
                        <ul className="space-y-1 text-sm max-h-40 overflow-y-auto">
                          {data.issues.no_meta_title.map((item, idx) => (
                            <li key={idx} className="text-gray-600">
                              <code className="text-xs bg-gray-100 px-1 rounded">{item.sku}</code> - {item.name}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-green-600">No issues found</p>
                      )}
                    </div>

                    {/* No Meta Description */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Tag className="h-5 w-5 text-yellow-600" />
                        <h4 className="font-medium text-gray-900">No Meta Description</h4>
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                          {data.issues?.no_meta_description?.length || 0}
                        </span>
                      </div>
                      {data.issues?.no_meta_description?.length > 0 ? (
                        <ul className="space-y-1 text-sm max-h-40 overflow-y-auto">
                          {data.issues.no_meta_description.map((item, idx) => (
                            <li key={idx} className="text-gray-600">
                              <code className="text-xs bg-gray-100 px-1 rounded">{item.sku}</code> - {item.name}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-green-600">No issues found</p>
                      )}
                    </div>

                    {/* Short Description */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Type className="h-5 w-5 text-orange-600" />
                        <h4 className="font-medium text-gray-900">Short Description</h4>
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs">
                          {data.issues?.short_description?.length || 0}
                        </span>
                      </div>
                      {data.issues?.short_description?.length > 0 ? (
                        <ul className="space-y-1 text-sm max-h-40 overflow-y-auto">
                          {data.issues.short_description.map((item, idx) => (
                            <li key={idx} className="text-gray-600">
                              <code className="text-xs bg-gray-100 px-1 rounded">{item.sku}</code> - {item.name}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-green-600">No issues found</p>
                      )}
                    </div>

                    {/* Missing Price */}
                    <div className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <DollarSign className="h-5 w-5 text-red-600" />
                        <h4 className="font-medium text-gray-900">Missing Price</h4>
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs">
                          {data.issues?.missing_price?.length || 0}
                        </span>
                      </div>
                      {data.issues?.missing_price?.length > 0 ? (
                        <ul className="space-y-1 text-sm max-h-40 overflow-y-auto">
                          {data.issues.missing_price.map((item, idx) => (
                            <li key={idx} className="text-gray-600">
                              <code className="text-xs bg-gray-100 px-1 rounded">{item.sku}</code> - {item.name}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-green-600">No issues found</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="rounded-lg bg-blue-50 p-4 border border-blue-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Scoring Criteria</p>
                  <p>Products are scored based on: description length (30pts), short description (10pts), images (20pts), meta title (10pts), meta description (15pts), price (10pts), and name quality (5pts). A score of 90+ is excellent, 70-89 is good, 50-69 needs improvement, and below 50 is poor.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductQuality;
